/**
 * "Zoom no longer lists this account" — the retirement badge.
 *
 * Shown on every surface a human explicitly ASKED for an account: omnibox search, Customer-360, and the
 * renewal / churn / trial worklists. Those lists are never filtered — a CSM chasing "Department of Disease
 * Control, MOPH, 55 days overdue" (row #1 of the trial list, and the one retired row in it) needs to know
 * it churned. That is information, not noise. Only the pure sell-more lists hide retired accounts, and
 * that filter lives in the DB view, not here.
 *
 * TONE: `warn`, deliberately. This is a FACT, not an error — `danger` would read as "something broke",
 * and `neutral` would be missed entirely on a busy table. StatusPill carries a dot AND text, so colour is
 * never the only signal.
 *
 * FAIL-SAFE: renders NOTHING unless is_active is EXACTLY false. Absent (a role/payload that doesn't carry
 * the flag) or true → no badge. An account we cannot confirm is retired is never accused of being retired.
 */

import { useTranslation } from "react-i18next";
import { StatusPill } from "@/components/ui";
import { formatDate } from "@/lib/format";

/**
 * The `| undefined` on each prop is deliberate, not noise. The repo runs `exactOptionalPropertyTypes`,
 * under which a bare `is_active?: boolean` means "the KEY may be absent" — but callers here legitimately
 * pass the value through from a payload that may not carry it (`pickBoolean` returns `boolean | undefined`,
 * and the money-stripped / older payloads simply omit the field). Widening the prop is the honest fix;
 * a `!` or a cast would just be lying to the checker about data that really can be missing.
 */
export interface RetiredBadgeProps {
  is_active?: boolean | undefined;
  left_list_at?: string | null | undefined;
  /** Compact form for dense rows (search results, worklist tables): short label, full sentence on hover. */
  compact?: boolean | undefined;
  className?: string | undefined;
}

export function RetiredBadge({
  is_active,
  left_list_at,
  compact = false,
  className,
}: RetiredBadgeProps) {
  const { t } = useTranslation();

  // `is_active !== false` covers BOTH "true" and "absent". Never badge on a missing flag.
  if (is_active !== false) return null;

  // left_list_at is set for every retired account by the sweep, but a null must not print "since —".
  const full = left_list_at
    ? t("retired.badgeSince", { date: formatDate(left_list_at) })
    : t("retired.badge");

  return (
    <StatusPill tone="warn" {...(className ? { className } : {})}>
      <span {...(compact ? { title: full } : {})}>{compact ? t("retired.badgeShort") : full}</span>
    </StatusPill>
  );
}
